import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import validate from 'validate.js';
import { makeStyles } from '@material-ui/styles';
import styles from './LoginComponentStyle.js';
import InputModal from 'components/InputModal';
import {
  Grid,
  Button,
  TextField,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress
} from '@material-ui/core';
import { Info, Visibility, VisibilityOff } from '@material-ui/icons';
import InputMask from 'react-input-mask';
import Footer from 'components/Footer/index.js';
import { Autocomplete } from '@material-ui/lab';
import { resetAdminMessage } from 'store/user/actions.js';
import { SelectInput } from 'components';

const useStyles = makeStyles(styles);

const LoginComponent = props => {
  const {
    title,
    subTitle,
    inputFields,
    buttonGroup,
    isLoginPage,
    isDisabled,
    schema,
    adsamlLogin,
    inputModalAttrributes,
    setIsOpen,
    isOpen
  } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { isLoading, adminMessage, error } = useSelector(store => store.user);
  const [showPassword, setShowPassword] = useState({});
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [formState, setFormState] = useState({
    isValid: false,
    values: {},
    touched: {},
    errors: {}
  });
  const { concurrentMessage, inputModaltitle, getInputButtonsGroup, modalShowCondition } = inputModalAttrributes || {};
  const validates = () => {
    const errors = validate(formState.values, schema);
    setFormState(formState => ({
      ...formState,
      isValid:
        (errors ? false : true) && Object.keys(formState.values).length > 0,
      errors: errors || {}
    }));
  };

  useEffect(() => {
    validates();
  }, [formState.values]);

  const handleChange = event => {
    dispatch(resetAdminMessage());
    setIsSubmitClicked(false);
    event.persist();
    if (event.target.value === '') {
      let formData = formState;
      delete formData.values[event.target.name];
      delete formData.touched[event.target.name];
      setFormState(form => ({
        ...form,
        values: form.values,
        touched: form.touched
      }));
    } else {
      setFormState(form => ({
        ...form,
        values: {
          ...form.values,
          [event.target.name]: event.target.value
        },
        touched: {
          ...form.touched,
          [event.target.name]: true
        }
      }));
    }
    validates();
  };

  const handleClickShowPassword = name => {
    showPassword[name] = !showPassword[name];
    setShowPassword({ ...showPassword });
  };

  const hasError = field =>
    formState.touched[field] && formState.errors[field] ? true : false;

  const disableState = () =>
    adminMessage && adminMessage.success && isSubmitClicked;

  const geLogoUrl = () => {
    let customerLogo = window.localStorage.getItem('url');
    let defaultLogo = 'images/logos/new_product_logo_color_white.png';
    return customerLogo || defaultLogo;
  };

  const getIsDisabled = name => {
    if (typeof isDisabled === 'function') {
      return isDisabled(name, formState.values);
    }
    return false;
  };

  const onButtonClick = (button, event) => {
    if (button.isSubmit) {
      setIsSubmitClicked(true);
      if (setIsOpen) {
        setIsOpen(true);
      }
    }
    button.action(event, formState.values);
  };

  const getInputProps = (inputField, appendValue) => {
    return {
      className: classes.inputField,
      variant: 'outlined',
      fullWidth: true,
      label: inputField.label,
      name: inputField.name,
      helperText: hasError(inputField.name)
        ? formState.errors[inputField.name][0]
        : null,
      error: hasError(inputField.name),
      ...(appendValue && { value: formState.values[inputField.name] || '' }),
      ...(inputField.customStyles && { style: inputField.customStyles })
    };
  };

  const getTypographyElement = inputField => {
    return (
      <Typography className={classes.typographyLabel} variant="h4">
        {inputField.label}
      </Typography>
    );
  };

  const getTextElement = inputField => {
    return inputField.isNumber ? (
      <InputMask
        mask="999-999-9999"
        onChange={handleChange}
        value={formState.values[inputField.name] || ''}
        disabled={getIsDisabled(inputField.name) || disableState()}
        maskPlaceholder={' '}>
        <TextField
          {...getInputProps(inputField, false)}
          type="text"
          required={!getIsDisabled(inputField.name)}
        />
      </InputMask>
    ) : (
      <TextField
        {...getInputProps(inputField, true)}
        onChange={handleChange}
        type="text"
        disabled={getIsDisabled(inputField.name) || disableState()}
        required={!getIsDisabled(inputField.name)}
      />
    );
  };

  const getPasswordElement = inputField => {
    return (
      <TextField
        {...getInputProps(inputField, true)}
        autoComplete="off"
        disabled={disableState()}
        onChange={handleChange}
        type={showPassword[inputField.name] ? 'text' : 'password'}
        required
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => handleClickShowPassword(inputField.name)}
                onMouseDown={event => event.preventDefault()}>
                {showPassword[inputField.name] ? (
                  <Visibility />
                ) : (
                  <VisibilityOff />
                )}
              </IconButton>
            </InputAdornment>
          )
        }}
      />
    );
  };

  const getAutocompleteElement = inputField => {
    return (
      <Autocomplete
        fullWidth
        options={inputField.options}
        getOptionLabel={option => option?.label}
        onChange={inputField.action}
        renderInput={params => (
          <TextField
            {...params}
            type="text"
            variant="outlined"
            label={inputField.label}
          />
        )}
      />
    );
  };

  const getDropdownOptions = options => {
    return {
      list: options,
      keyId: 'value',
      valueId: 'value',
      displayValueId: 'label'
    };
  };

  const getDropDownElement = inputField => {
    return (
      <SelectInput
        {...getInputProps(inputField, true)}
        disabled={disableState()}
        onChange={handleChange}
        customStyles={{ marginTop: '16px' }}
        options={getDropdownOptions(inputField.options)}
      />
    );
  };

  const getLinkElement = inputField => {
    return (
      <React.Fragment>
        <br />
        {inputField.isClickable ? (
          <Link
            className={classes.hyperlink}
            variant="h6"
            onClick={() => {
              setIsSubmitClicked(false);
              inputField.action();
            }}>
            {inputField.label}
          </Link>
        ) : (
          <React.Fragment>
            <Link className={classes.highlight} variant="h6">
              {inputField.label}
            </Link>
            <Tooltip title={inputField.tooltip}>
              <Info className={classes.infoIcon} />
            </Tooltip>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  };

  const renderInputFields = () => {
    return inputFields?.map((inputField, index) => {
      return (
        <React.Fragment key={index}>
          {inputField.type === 'typography' && getTypographyElement(inputField)}
          {inputField.type === 'text' && getTextElement(inputField)}
          {inputField.type === 'password' && getPasswordElement(inputField)}
          {inputField.type === 'autocomplete' &&
            getAutocompleteElement(inputField)}
          {inputField.type === 'select' && getDropDownElement(inputField)}
          {inputField.type === 'link' && getLinkElement(inputField)}
        </React.Fragment>
      );
    });
  };

  const renderButton = (button, index) => {
    return (
      <Button
        key={index}
        className={button.isSsoButton ? classes.ssoButton : classes.button}
        color="primary"
        disabled={
          (!button.isSsoButton && !formState.isValid && !button.isEnabled) ||
          (button.isSubmit && isSubmitClicked)
        }
        fullWidth
        size="large"
        onClick={event => onButtonClick(button, event)}
        variant="contained"
        {...(button.isSubmit && { type: 'submit' })}>
        {button.title}
      </Button>
    );
  };

  const getloginPageFooter = () => {
    return (
      <Typography
        color="textSecondary"
        variant="body1"
        className={classes.forgotLink}
        style={{ textAlign: 'left' }}>
        Forgot{' '}
        <Link
          component={RouterLink}
          to="/forgot/username"
          variant="h6"
          style={{ color: '#7bcbe7' }}>
          User ID
        </Link>{' '}
        or{' '}
        <Link
          component={RouterLink}
          to="/forgot/password"
          variant="h6"
          style={{ color: '#7bcbe7' }}>
          Password
        </Link>
      </Typography>
    );
  };

  const onSsoClick = event => {
    event.preventDefault();
    history.push(adsamlLogin?.isEnabled ? '/sign-in' : '/ssoLogin');
  };

  const getAdsamlButton = () => [
    {
      title: `Login with ${adsamlLogin?.isEnabled ? 'Password' : 'SSO'}`,
      action: event => onSsoClick(event),
      isSsoButton: true
    }
  ];

  return (
    <div className={classes.root}>
      {modalShowCondition && (
        <InputModal
          title={inputModaltitle}
          setIsOpen={setIsOpen}
          isOpen={isOpen}
          message={concurrentMessage(formState.values.username)}
          inputModalbuttons={getInputButtonsGroup}
          formValues={formState.values}
        />
      )}

      <Grid className={classes.grid} container>
        <Grid className={classes.content} item lg={9} xs={12}>
          <div className={classes.header}>
            <Typography className={classes.title} variant="h2">
              {title}
            </Typography>
            {
              <Typography
                className={classes.para}
                color="textSecondary"
                gutterBottom>
                {subTitle}
              </Typography>
            }
          </div>
          <form
            method="post"
            className={classes.form}
            onSubmit={event =>
              onButtonClick(
                buttonGroup.filter(btn => btn.isSubmit),
                event
              )
            }>
            {isLoading || adsamlLogin?.isSamlLoading ? (
              <div className={classes.loader}>
                <CircularProgress />
              </div>
            ) : (
              <React.Fragment>
                <div>
                  {renderInputFields()}
                  <Typography
                    variant="subtitle2"
                    className={classes.typographyMargin}>
                    {adminMessage && (
                      <div
                        className={
                          adminMessage.success ? classes.success : classes.error
                        }>
                        {adminMessage.message}
                        <br />
                        {!adminMessage.success &&
                          !adminMessage.isSendCodeMessage &&
                          'Please contact system administrator for assistance.'}
                      </div>
                    )}
                    <div className={classes.error}>
                      {error &&
                        error.message &&
                        isSubmitClicked &&
                        error.message.instructions !== 'Session already exists' && error.message.instructions}
                      &nbsp;
                      {adsamlLogin?.isEnabled && adsamlLogin.error}
                    </div>
                  </Typography>
                </div>
                <div>
                  {buttonGroup.map((button, index) => {
                    return renderButton(button, index);
                  })}
                  {isLoginPage && getloginPageFooter()}
                  {(isLoginPage || adsamlLogin?.isEnabled) && (
                    <div>
                      <div className={classes.divider}>
                        <hr className={classes.line}></hr>
                        <span className={classes.typography}>{'or'}</span>
                        <hr className={classes.line}></hr>
                      </div>
                      <div>
                        {getAdsamlButton().map((button, index) => {
                          return renderButton(button, index);
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </React.Fragment>
            )}
          </form>
          <div className={classes.footer}>
            <div className={classes.footerImage}>
              <img
                src="images/logos/new_agilysys_logo_color_black.svg"
                className={classes.logo}
                alt="footer-img"
              />
              <br />
            </div>
            <Footer className={classes.copyrightFooter} />
          </div>
        </Grid>
        <Grid className={classes.imgArea} item lg={3} xs={2}>
          <img
            src={geLogoUrl()}
            alt="logo"
            className={classes.customerLogo}
            id="customer-logo"
          />
        </Grid>
      </Grid>
    </div>
  );
};

LoginComponent.propTypes = {
  title: PropTypes.string,
  subTitle: PropTypes.string,
  inputFields: PropTypes.array,
  buttonGroup: PropTypes.array,
  isLoginPage: PropTypes.bool,
  adsamlLogin: PropTypes.object,
  isDisabled: PropTypes.func,
  schema: PropTypes.object
};

export default LoginComponent;
